// Unpublished Work © 2021-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {Dialog} from '@deere/isg.component-library';
import ConfirmationDialog from 'Ui/components/dialogs/confirmation-dialog';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import ServiceFormInputs from 'OnEquip/equipment/service-form/service-form-inputs';
import ServiceIncompleteTasksDialog from 'OnEquip/equipment/service-form/service-incomplete-tasks-dialog';
import {
    createService,
    updateService,
    getService,
    getServiceTasks,
    getServiceTypes,
    postServiceLog
} from 'Services/maintenance-service';
import {
    getFilteredFleetEquipment,
    getFleetByMembershipId,
    getMembershipUsersByPermission
} from 'Services/membership-service';
import {getModelMaintenance} from 'Services/model-maintenance-service';
import permissionConstants from 'Ui/constants/permission-constants';
import serviceStatusConstants from 'Ui/constants/service-status-constants';
import {EDIT_SERVICE} from 'Common/constants/business-activities';
import logStateConstants from 'OnEquip/equipment/service-form/utils/log-state-constants';
import serviceGroupConstants from 'OnEquip/equipment/service-form/utils/service-group-constants';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {
    getStatusList,
    mapFormValuesToServiceData,
    mapServiceDataToFormValues,
    mapTypeDataToInputData,
    updateActualEngineHours,
    updateHoc,
    verifyEditableService
} from 'Ui/features/onequip/equipment/service-form/service-form-helper';
import {fetchEffectData} from 'Utils/react-utils';
import {redirect as redirectTo} from 'Utils/redirect-utils';
import {isNullOrUndefined} from 'Common/utils/validation-utils';
import moment from 'moment';
import {openServiceFormPdf} from './utils/service-form-pdf';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {fetchAndFilterInactiveOperators} from 'OnLabor/workboard/utils/fetch-operators';
import {connect} from 'react-redux';
import {MAINTENANCE_MANAGER_PROGOLF} from 'Common/constants/isg-toggles';

function initializeBooleanState() {
    const [loading, setLoading] = React.useState(true);
    const [isViewableService, setIsViewableService] = React.useState(true);
    const [showConfirmationDialog, setShowConfirmationDialog] = React.useState(false);
    const [showIncompleteServiceTasksDialog, setIncompleteServiceTasksDialog] = React.useState(false);

    return {
        loading,
        setLoading,
        isViewableService,
        setIsViewableService,
        showConfirmationDialog,
        setShowConfirmationDialog,
        showIncompleteServiceTasksDialog,
        setIncompleteServiceTasksDialog
    };
}

function initializeState(serviceId, equipmentId, translations) {
    const [values, setValues] = React.useState(() => ({
        actualDuration: '',
        assignedTo: [],
        atHours: '',
        dueAtDate: moment(),
        dueAtDateRequired: !serviceId,
        editableNote: '',
        estDuration: '',
        equipmentId,
        notes: [],
        partsList: [],
        serviceDate: moment(),
        serviceGroup: serviceGroupConstants.maintenance,
        serviceId,
        serviceTasks: [],
        serviceTasksCheckedIds: [],
        serviceTasksSelectedIds: [],
        serviceTypeId: null,
        status: serviceStatusConstants.recommended.id,
        totalHours: ''
    }));

    const [allData, setAllData] = React.useState(() => ({
        equipmentOptions: [],
        serviceAssignableUsers: [],
        serviceTasksOptions: [],
        serviceTypesOptions: []
    }));

    const [modelMaintenanceServiceTaskIds, setModelMaintenanceServiceTaskIds] = React.useState(() => new Set());
    const [statusCollection, setStatusCollection] = React.useState(() => getStatusList(serviceStatusConstants.recommended.id, translations));
    const [hoc, setHoc] = React.useState(null);
    const [editedServiceStatus, setEditedServiceStatus] = React.useState(null);
    const [skippedService, setSkippedService] = React.useState(null);
    const [currentEquipment, setCurrentEquipment] = React.useState(null);
    const [isPrint, setIsPrint] = React.useState(false);

    return {
        values,
        setValues,
        allData,
        setAllData,
        statusCollection,
        setStatusCollection,
        hoc,
        setHoc,
        editedServiceStatus,
        setEditedServiceStatus,
        skippedService,
        setSkippedService,
        fleetIdRef: React.useRef(),
        currentEquipment,
        setCurrentEquipment,
        ...initializeBooleanState(),
        modelMaintenanceServiceTaskIds,
        setModelMaintenanceServiceTaskIds,
        isPrint,
        setIsPrint
    };
}

async function getModelMaintenanceTaskIds(modelMaintenanceId) {
    const {modelMaintenance} = await getModelMaintenance(modelMaintenanceId);
    const modelMaintenanceTaskIds = new Set();

    modelMaintenance?.serviceTasks?.forEach(({serviceTaskTypeId}) => {
        modelMaintenanceTaskIds.add(serviceTaskTypeId);
    });

    return modelMaintenanceTaskIds;
}

function getCombinedUsers(service, users) {
    if (isNullOrUndefined(service?.users)) {
        return users;
    }

    const existingUsers = [];

    service.users.forEach((serviceUser) => {
        const userExists = users.find(({id}) => id === serviceUser.appUserId);

        if (!userExists || userExists.inactive) {
            const additionalVariables = userExists || serviceUser;

            existingUsers.push({
                ...additionalVariables,
                deleted: true,
                inactive: false
            });
        }
    });

    return [...users, ...existingUsers];
}

function getCombinedEquipment(service, equipment) {
    if (isNullOrUndefined(service?.equipmentId)) {
        return equipment;
    }

    const {
        equipmentId,
        equipmentName,
        manufacturerName,
        modelName
    } = service;

    const isEquipmentPresent = equipment.some((equipmentData) => equipmentData.equipmentId === equipmentId);

    if (!isEquipmentPresent) {
        equipment.push({
            equipmentId,
            equipmentName,
            manufacturerName,
            modelName
        });
    }

    return equipment;
}

async function getTechnicianStaff(teamMembers) {
    const technicianRoleIds = new Set(
        teamMembers?.filter((tm) =>
            tm.roles?.some((roleItem) =>
                roleItem.role.toUpperCase() === 'TECHNICIAN'
            )
        ).map((tm) => tm.id).filter(Boolean)
    );

    const filteredUsers = teamMembers?.filter((tm) => technicianRoleIds.has(tm.id));

    return filteredUsers;
}

async function getData(serviceId, {
    membershipId, orgId, isMigrated
}, translations) {
    const dataPromises = [
        getServiceTasks(),
        getFilteredFleetEquipment(),
        getServiceTypes(),
        getMembershipUsersByPermission(permissionConstants.ASSIGNED_TO_SERVICE_TICKETS),
        getFleetByMembershipId(membershipId),
        fetchAndFilterInactiveOperators(orgId)
    ];

    if (serviceId) {
        dataPromises.push(getService(serviceId));
    }

    const [
        {serviceTaskTypes},
        equipment,
        {serviceTypes},
        {users},
        {fleets},
        teamMembers,
        editableService
    ] = await Promise.all(dataPromises);

    let staff = users;

    if (isMigrated && orgId) {
        staff = await getTechnicianStaff(teamMembers);
    }

    const existingService = editableService?.service;
    const combinedUsers = getCombinedUsers(existingService, staff);
    const combinedEquipment = getCombinedEquipment(existingService, equipment);

    const inputData = {
        editableService: existingService,
        ...mapTypeDataToInputData(combinedUsers, combinedEquipment, serviceTypes, serviceTaskTypes, translations, existingService?.serviceTypeId),
        fleetId: fleets[0].fleetId
    };

    return existingService?.modelMaintenanceId ? {
        ...inputData,
        modelMaintenanceServiceTaskIds: await getModelMaintenanceTaskIds(existingService.modelMaintenanceId)
    } : inputData;
}

async function saveServiceForm(serviceId, values, allData, updateValues) {
    const serviceData = mapFormValuesToServiceData(values, allData);

    const newService = serviceId ?
        await updateService(serviceData, serviceId) :
        await createService(serviceData);

    updateValues(newService.skippedService || newService.service);

    return newService;
}

function ServiceForm(props) {
    const {
        closeDialogHandler,
        equipmentId,
        history,
        invalidInputs,
        isgToggles,
        membership,
        myJdPermissions,
        onSaveHandler,
        redirect,
        serviceId,
        setValid,
        translations
    } = props;

    const {
        values,
        setValues,
        loading,
        setLoading,
        allData,
        setAllData,
        statusCollection,
        setStatusCollection,
        hoc,
        setHoc,
        isViewableService,
        setIsViewableService,
        editedServiceStatus,
        setEditedServiceStatus,
        showConfirmationDialog,
        setShowConfirmationDialog,
        showIncompleteServiceTasksDialog,
        setIncompleteServiceTasksDialog,
        skippedService,
        setSkippedService,
        fleetIdRef,
        currentEquipment,
        setCurrentEquipment,
        modelMaintenanceServiceTaskIds,
        setModelMaintenanceServiceTaskIds,
        isPrint,
        setIsPrint
    } = initializeState(serviceId, equipmentId, translations);

    const {addToast} = useToast();

    const isFleetToMaintenanceEnabled = isgToggles?.[MAINTENANCE_MANAGER_PROGOLF];

    async function editableServiceInitialize(editableService, {
        mappedEquipment = allData.equipmentOptions,
        modelMaintenanceServiceTaskIdsData = modelMaintenanceServiceTaskIds
    } = {}) {
        const initialValues = mapServiceDataToFormValues(editableService);

        verifyEditableService(initialValues, mappedEquipment, setIsViewableService, setCurrentEquipment);

        setValues((prevValues) => ({
            ...prevValues,
            ...initialValues
        }));
        setStatusCollection(getStatusList(editableService.status, translations));
        setEditedServiceStatus(editableService.status);
        setModelMaintenanceServiceTaskIds(modelMaintenanceServiceTaskIdsData);

        await updateHoc(mappedEquipment, initialValues.equipmentId, initialValues.serviceDate, membership, setHoc, translations);
    }

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setLoading(true);

        const {
            editableService,
            mappedEquipment,
            mappedServiceAssignableUsers,
            mappedServiceTasks,
            mappedServiceTypes,
            modelMaintenanceServiceTaskIds: modelMaintenanceServiceTaskIdsData,
            fleetId
        } = await getData(values.serviceId, membership, translations);

        fleetIdRef.current = fleetId;

        if (editableService && isMounted()) {
            await editableServiceInitialize(editableService, {
                mappedEquipment,
                modelMaintenanceServiceTaskIdsData
            });
        }

        if (isMounted()) {
            setAllData({
                equipmentOptions: mappedEquipment,
                serviceAssignableUsers: mappedServiceAssignableUsers,
                serviceTasksOptions: mappedServiceTasks,
                serviceTypesOptions: mappedServiceTypes
            });

            setLoading(false);

            if (!editableService && values.equipmentId) {
                await updateHoc(mappedEquipment, values.equipmentId, values.serviceDate, membership, setHoc, translations);
                updateActualEngineHours(mappedEquipment, values.equipmentId, setValues, setCurrentEquipment);
            }
        }
    }), []);

    function cancelServiceUpdate() {
        if (closeDialogHandler) {
            setIsPrint(false);
            closeDialogHandler();
        } else {
            const redirectUrl = redirect.path
                .replace('{membershipId}', membership.membershipId)
                .replace('{fleetId}', fleetIdRef.current)
                .replace('{equipmentId}', equipmentId || values.equipmentId);

            redirectTo(redirectUrl, redirect.reactRedirect ? history : null);
        }
    }

    function closeOnSave() {
        cancelServiceUpdate();

        if (onSaveHandler) {
            onSaveHandler();
        }
    }

    // eslint-disable-next-line max-statements
    async function serviceFormOnSave(displayConfirmationDialog, doPrint = false, forceComplete = false) {
        setLoading(true);

        const targetStatus = forceComplete ? serviceStatusConstants.completed.id : values.status;
        const isCompletingTicket = targetStatus === serviceStatusConstants.completed.id && editedServiceStatus !== serviceStatusConstants.completed.id;

        if (isCompletingTicket &&
            displayConfirmationDialog) {
            setShowConfirmationDialog(true);
            setIsPrint(doPrint);
        } else {
            if (isCompletingTicket && values.serviceId) {
                await postServiceLog(values.serviceId, logStateConstants.stop);
            }

            const saveValues = forceComplete ? {
                ...values,
                status: serviceStatusConstants.completed.id
            } : values;
            const newService = await saveServiceForm(values.serviceId, saveValues, allData, editableServiceInitialize);
            const printing = doPrint || isPrint;

            if (printing) {
                openServiceFormPdf(values, allData, currentEquipment, translations);
                setIsPrint(false);
                setValues({
                    ...values,
                    editableNote: ''
                });
                setShowConfirmationDialog(false);
            }

            if (!displayConfirmationDialog && newService.skippedService) {
                setSkippedService(newService.skippedService);
                setIncompleteServiceTasksDialog(true);
            } else if (!printing) {
                closeOnSave();
            }
        }

        setLoading(false);
    }

    const {
        readOnly, disabled
    } = React.useMemo(() => ({
        readOnly: isFleetToMaintenanceEnabled || !isAuthorized({
            myJdPermissions: EDIT_SERVICE
        }, {
            isMigrated: membership.isMigrated,
            myJdPermissions
        }),
        disabled: invalidInputs.size > 0 || loading
    }), [membership.isMigrated, myJdPermissions, invalidInputs.size, loading, isFleetToMaintenanceEnabled]);

    const footerActions = [{
        title: readOnly ? translations.CLOSE : translations.CANCEL,
        variant: 'secondary',
        onClick: cancelServiceUpdate
    }, !isFleetToMaintenanceEnabled && {
        title: translations.save,
        loading,
        disabled,
        variant: 'primary',
        onClick: async () => {
            try {
                await serviceFormOnSave(true);
            } catch {
                addToast({
                    message: translations.ERROR_OCCURRED_TITLE,
                    type: TOAST_TYPE.ERROR
                });
            }
        }
    }].filter(Boolean);

    const leftFooterActions = membership.isMigrated ?
        [{
            title: translations.PRINT,
            onClick: readOnly ?
                () => openServiceFormPdf(values, allData, currentEquipment, translations) :
                async () => {
                    setIsPrint(true);
                    await serviceFormOnSave(true, true);
                }
        }] : [];

    const footerMessage = `* = ${translations.REQUIRED}`;

    return (
        <>
            <Dialog
                className='service-form onlink-dialog'
                closeHandler={cancelServiceUpdate}
                footerActions={footerActions}
                footerMessage={footerMessage}
                leftFooterActions={leftFooterActions}
                open={true}
                title={translations.ONLINK_SERVICE_TICKET}
                width={false}
            >
                {
                    isViewableService ? (
                        <ServiceFormInputs
                            allData={allData}
                            currentEquipment={currentEquipment}
                            editedServiceStatus={editedServiceStatus}
                            hoc={hoc}
                            invalidInputs={invalidInputs}
                            isMigrated={membership.isMigrated}
                            loading={loading}
                            membership={membership}
                            modelMaintenanceServiceTaskIds={modelMaintenanceServiceTaskIds}
                            readOnly={readOnly}
                            serviceId={serviceId}
                            setCurrentEquipment={setCurrentEquipment}
                            setHoc={setHoc}
                            setValid={setValid}
                            setValues={setValues}
                            statusCollection={statusCollection}
                            timerStatusChange={async (newStatusValue) => {
                                if (!values.serviceId || newStatusValue === serviceStatusConstants.in_process.id) {
                                    const createInProcessService = {
                                        ...values,
                                        status: serviceStatusConstants.in_process.id
                                    };

                                    const createdService = await saveServiceForm(values.serviceId, createInProcessService,
                                        allData, editableServiceInitialize);

                                    await postServiceLog(createdService.service.serviceId, logStateConstants.start);
                                }

                                if (newStatusValue === serviceStatusConstants.completed.id) {
                                    setShowConfirmationDialog(true);
                                }
                            }}
                            translations={translations}
                            values={values}
                        />
                    ) : (
                        <div className='service-form-error-view'>
                            {translations.ONLINK_SERVICE_TICKET_NOT_FOUND}
                        </div>
                    )
                }
            </Dialog>
            {
                showConfirmationDialog &&
                <ConfirmationDialog
                    cancelTitle={translations.CANCEL}
                    continueTitle={translations.ONLINK_MARK_AS_COMPLETE}
                    message={translations.ONLINK_SERVICE_COMPLETION_MESSAGE}
                    onCancel={() => {
                        setShowConfirmationDialog(false);
                    }}
                    onContinue={() => serviceFormOnSave(false, false, true)}
                    title={translations.confirm}
                />
            }
            {
                showIncompleteServiceTasksDialog &&
                <ServiceIncompleteTasksDialog
                    checkListSelectedIds={values.serviceTasksCheckedIds}
                    multiSelectSelectedIds={values.serviceTasksSelectedIds}
                    redirect={closeOnSave}
                    serviceTasksData={allData.serviceTasksOptions}
                    setValid={setValid}
                    skippedService={skippedService}
                    translations={translations}
                />
            }
        </>
    );
}

export function mapStateToProps(state) {
    return {
        isgToggles: state.account?.isgToggles
    };
}

ServiceForm.propTypes = {
    closeDialogHandler: PropTypes.func,
    equipmentId: PropTypes.string,
    history: PropTypes.history,
    invalidInputs: PropTypes.instanceOf(Set),
    isgToggles: PropTypes.object,
    membership: PropTypes.membership,
    myJdPermissions: PropTypes.myJdPermissions,
    onSaveHandler: PropTypes.func,
    redirect: PropTypes.object,
    serviceId: PropTypes.string,
    setValid: PropTypes.func,
    translations: PropTypes.translations
};

export default connect(mapStateToProps)(FormValidator(ServiceForm));
